import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar, AlertCircle, Send, MessageSquare, Clock, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import LossReasonDialog from '../components/LossReasonDialog';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUCKETS = [7, 15, 30, 60, 90];

const addDaysISO = (isoDate, days) => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const Renewals = () => {
  const [data, setData] = useState({ 7: [], 30: [], 60: [], 90: [] });
  const [lostRenewals, setLostRenewals] = useState([]);
  const [gracePolicies, setGracePolicies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('7days');
  const [selected, setSelected] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [bulkChannel, setBulkChannel] = useState('sms');
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [waSendingId, setWaSendingId] = useState('');
  // Custom date range (Item 5)
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [customRows, setCustomRows] = useState(null); // null → not queried yet
  const [customLoading, setCustomLoading] = useState(false);
  // Renewal action state
  const [actionOpen, setActionOpen] = useState(false);
  const [actionPolicy, setActionPolicy] = useState(null);
  const [actionMode, setActionMode] = useState('renewed'); // 'renewed' | 'lost'
  const [actionForm, setActionForm] = useState({ new_start_date: '', new_end_date: '', new_next_due_date: '', new_policy_number: '', new_sum_assured: '', new_premium_amount: '' });
  const [actionSaving, setActionSaving] = useState(false);
  const [quickLostId, setQuickLostId] = useState('');
  const [lostReasonTarget, setLostReasonTarget] = useState(null);
  const [lostReasonSaving, setLostReasonSaving] = useState(false);

  // Memoise template dropdown so we don't re-filter on every keystroke elsewhere
  const bulkChannelTemplates = useMemo(
    () => templates.filter(t => t.channel === 'both' || t.channel === bulkChannel),
    [templates, bulkChannel]
  );

  const loadAll = useCallback(async () => {
    try {
      const [renewalsRes, graceRes, statsRes, lostRes] = await Promise.all([
        Promise.all(BUCKETS.map(d => axios.get(`${API}/renewals?days=${d}`, { withCredentials: true }))),
        axios.get(`${API}/policies/grace-period`, { withCredentials: true }),
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/renewals/lost`, { withCredentials: true }),
      ]);
      const next = {};
      BUCKETS.forEach((d, i) => { next[d] = renewalsRes[i].data; });
      setData(next);
      setGracePolicies(graceRes.data || []);
      setStats(statsRes.data || null);
      setLostRenewals(lostRes.data || []);
    } catch (e) {
      console.error('Failed to load renewals:', e);
      toast.error(e.response?.data?.detail || 'Failed to load renewals');
    } finally { setLoading(false); }
  }, []);

  const buildReminderBody = (r) => {
    const due = r.next_due_date ? new Date(r.next_due_date) : null;
    const dueFmt = due && !Number.isNaN(due.getTime())
      ? `${String(due.getDate()).padStart(2,'0')}/${String(due.getMonth()+1).padStart(2,'0')}/${due.getFullYear()}`
      : 'soon';
    const days = typeof r.days_remaining === 'number' ? r.days_remaining : '';
    return `Hi ${r.client_name || 'there'}, this is your agent from InsureGrowth. `
      + `Your policy ${r.policy_number} is due for renewal on ${dueFmt}`
      + (days !== '' ? ` — just ${days} day${days === 1 ? '' : 's'} away.` : '.')
      + ` Please let me know a convenient time to complete the renewal. Thank you.`;
  };

  const sendWhatsAppReminder = async (r) => {
    if (!r.client_phone) { toast.error('No mobile on file for this client'); return; }
    setWaSendingId(r.policy_id);
    try {
      await axios.post(`${API}/messages/send`, {
        to: r.client_phone,
        body: buildReminderBody(r),
        channel: 'whatsapp',
        client_id: r.client_id,
      }, { withCredentials: true });
      toast.success(`WhatsApp reminder sent to ${r.client_name}`);
    } catch (err) {
      console.error('WhatsApp reminder failed:', err);
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 503) {
        // Twilio not configured — deep-link to fix it.
        toast.error(detail || 'Twilio not configured', {
          duration: 8000,
          action: { label: 'Set up Twilio', onClick: () => { window.location.href = '/settings'; } },
        });
      } else {
        toast.error(detail || 'WhatsApp send failed');
      }
    } finally { setWaSendingId(''); }
  };

  // Load Twilio configured-status once to show a persistent banner if unset.
  const [twilioConfigured, setTwilioConfigured] = useState(true);
  useEffect(() => {
    axios.get(`${API}/messages/twilio-status`, { withCredentials: true })
      .then(r => setTwilioConfigured(Boolean(r.data?.configured)))
      .catch(() => setTwilioConfigured(true));
  }, []);

  useEffect(() => {
    loadAll();
    axios.get(`${API}/message-templates`, { withCredentials: true })
      .then(r => setTemplates(r.data))
      .catch((err) => console.warn('Templates load failed:', err?.message));
  }, [loadAll]);

  const currentBucket = () => Number(activeTab.replace('days', ''));

  const toggleSelect = (policyId) => {
    setSelected(prev => ({ ...prev, [policyId]: !prev[policyId] }));
  };

  const toggleSelectAll = () => {
    const list = data[currentBucket()] || [];
    const allSelected = list.length > 0 && list.every(r => selected[r.policy_id]);
    if (allSelected) {
      const next = { ...selected };
      list.forEach(r => { delete next[r.policy_id]; });
      setSelected(next);
    } else {
      const next = { ...selected };
      list.forEach(r => { next[r.policy_id] = true; });
      setSelected(next);
    }
  };

  const selectedRenewals = () => {
    const list = data[currentBucket()] || [];
    return list.filter(r => selected[r.policy_id]);
  };

  const openBulk = () => {
    if (selectedRenewals().length === 0) { toast.error('Select at least one renewal first'); return; }
    setBulkResult(null);
    setBulkOpen(true);
  };

  const sendBulk = async () => {
    if (!bulkTemplateId) { toast.error('Pick a template'); return; }
    const clientIds = [...new Set(selectedRenewals().map(r => r.client_id))];
    setBulkSending(true);
    try {
      const { data: res } = await axios.post(`${API}/messages/bulk-send`, {
        channel: bulkChannel,
        client_ids: clientIds,
        template_id: bulkTemplateId,
      }, { withCredentials: true });
      setBulkResult(res);
      const summary = `${res.sent} sent · ${res.failed} failed · ${res.skipped} skipped`;
      if (res.sent > 0) toast.success(summary); else toast.error(summary);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk send failed');
    } finally { setBulkSending(false); }
  };

  const getUrgencyColor = (days) => {
    if (days <= 7) return 'bg-red-100 text-red-800';
    if (days <= 15) return 'bg-orange-100 text-orange-800';
    if (days <= 30) return 'bg-yellow-100 text-yellow-800';
    return 'bg-blue-100 text-blue-800';
  };

  const RenewalsTable = ({ bucket }) => {
    const renewals = data[bucket] || [];
    if (renewals.length === 0) return <p className="text-center text-slate-500 py-8">No renewals in this period</p>;
    const allSelected = renewals.every(r => selected[r.policy_id]);
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid="renewals-select-all" />
            </TableHead>
            <TableHead>Policy Number</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>Product</TableHead>
            <TableHead>Premium</TableHead>
            <TableHead>Due Date</TableHead>
            <TableHead>Days Left</TableHead>
            <TableHead>Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {renewals.map((r) => (
            <TableRow key={r.policy_id} data-testid={`renewal-row-${r.policy_id}`}>
              <TableCell>
                <Checkbox
                  checked={!!selected[r.policy_id]}
                  onCheckedChange={() => toggleSelect(r.policy_id)}
                  data-testid={`renewal-select-${r.policy_id}`}
                />
              </TableCell>
              <TableCell className="font-medium">{r.policy_number}</TableCell>
              <TableCell>
                <div>
                  <p className="font-medium">{r.client_name}</p>
                  <p className="text-xs text-slate-500">{r.client_phone}</p>
                </div>
              </TableCell>
              <TableCell>
                <div>
                  <p className="font-medium">{r.product_name}</p>
                  <p className="text-xs text-slate-500">{r.product_type}</p>
                </div>
              </TableCell>
              <TableCell>{formatCurrency(r.premium_amount)}</TableCell>
              <TableCell>{formatDate(r.next_due_date)}</TableCell>
              <TableCell><Badge className={getUrgencyColor(r.days_remaining)}>{r.days_remaining} days</Badge></TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-green-600 text-green-700 hover:bg-green-50 h-7 px-2"
                    disabled={waSendingId === r.policy_id || !r.client_phone}
                    onClick={() => sendWhatsAppReminder(r)}
                    data-testid={`row-wa-reminder-${r.policy_id}`}
                    title={r.client_phone ? 'Send WhatsApp reminder' : 'No mobile on file'}
                  >
                    <MessageSquare className="h-3.5 w-3.5 mr-1" />
                    {waSendingId === r.policy_id ? 'Sending…' : 'WhatsApp'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-700 text-blue-800 hover:bg-blue-50 h-7 px-2"
                    onClick={() => openAction(r)}
                    data-testid={`row-update-${r.policy_id}`}
                    title="Mark as Renewed or Lost"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Update
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-600 text-red-700 hover:bg-red-50 h-7 px-2"
                    disabled={quickLostId === r.policy_id}
                    onClick={() => quickMarkLost(r)}
                    data-testid={`row-mark-lost-${r.policy_id}`}
                    title="One-click: mark policy as Lost (moves into Lost Leads)"
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1" />
                    {quickLostId === r.policy_id ? '...' : 'Mark Lost'}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const openAction = (policy) => {
    setActionPolicy(policy);
    setActionMode('renewed');
    const today = new Date().toISOString().slice(0, 10);
    const start = today;
    const end = addDaysISO(start, 364);
    setActionForm({
      new_start_date: start,
      new_end_date: end,
      new_next_due_date: end,
      new_policy_number: policy.policy_number || '',
      new_sum_assured: policy.sum_assured || '',
      new_premium_amount: policy.premium_amount || '',
    });
    setActionOpen(true);
  };

  const onActionStartChange = (v) => {
    setActionForm(f => {
      const prevAuto = f.new_start_date ? addDaysISO(f.new_start_date, 364) : '';
      const syncEnd = !f.new_end_date || f.new_end_date === prevAuto;
      const nextEnd = syncEnd ? addDaysISO(v, 364) : f.new_end_date;
      const syncDue = !f.new_next_due_date || f.new_next_due_date === prevAuto || f.new_next_due_date === f.new_end_date;
      return { ...f, new_start_date: v, new_end_date: nextEnd, new_next_due_date: syncDue ? nextEnd : f.new_next_due_date };
    });
  };

  const submitAction = async () => {
    if (!actionPolicy) return;
    setActionSaving(true);
    try {
      if (actionMode === 'renewed') {
        if (!actionForm.new_start_date) { toast.error('New start date is required'); setActionSaving(false); return; }
        const payload = {
          new_start_date: actionForm.new_start_date,
          new_end_date: actionForm.new_end_date || null,
          new_next_due_date: actionForm.new_next_due_date || null,
          new_policy_number: actionForm.new_policy_number || null,
          new_sum_assured: actionForm.new_sum_assured === '' ? null : parseFloat(actionForm.new_sum_assured),
          new_premium_amount: actionForm.new_premium_amount === '' ? null : parseFloat(actionForm.new_premium_amount),
        };
        await axios.post(`${API}/policies/${actionPolicy.policy_id}/mark-renewed`, payload, { withCredentials: true });
        toast.success('Policy marked as Renewed');
      } else {
        await axios.post(`${API}/policies/${actionPolicy.policy_id}/mark-lost`, {}, { withCredentials: true });
        toast.success('Policy marked as Lost');
      }
      setActionOpen(false);
      setActionPolicy(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setActionSaving(false); }
  };

  const quickMarkLost = (r) => {
    setLostReasonTarget(r);
  };

  const submitQuickMarkLost = async (reason, notes) => {
    if (!lostReasonTarget) return;
    setLostReasonSaving(true);
    setQuickLostId(lostReasonTarget.policy_id);
    try {
      await axios.post(`${API}/policies/${lostReasonTarget.policy_id}/mark-lost`, {
        loss_reason: reason || null, loss_notes: notes || null,
      }, { withCredentials: true });
      toast.success('Policy marked as Lost', {
        description: 'View it on the Lost tab (Renewals) or on Lost Leads.',
        action: { label: 'Open Lost tab', onClick: () => { setActiveTab('lost'); } },
      });
      setLostReasonTarget(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark policy lost');
    } finally { setLostReasonSaving(false); setQuickLostId(''); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner"></div></div>;

  const selectedCount = selectedRenewals().length;

  return (
    <div className="space-y-6" data-testid="renewals-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Policy Renewals</h1>
          <p className="text-slate-600 mt-1">Track and manage upcoming policy renewals</p>
        </div>
        <Button
          onClick={openBulk}
          disabled={selectedCount === 0}
          className="bg-blue-800 hover:bg-blue-900 disabled:opacity-40"
          data-testid="bulk-send-open-button"
        >
          <Send className="h-4 w-4 mr-2" />
          Bulk Send {selectedCount > 0 ? `(${selectedCount})` : ''}
        </Button>
      </div>

      {!twilioConfigured && (
        <Alert className="bg-amber-50 border-amber-200" data-testid="renewals-twilio-banner">
          <AlertCircle className="h-4 w-4 text-amber-700" />
          <AlertDescription className="text-amber-900 flex items-center justify-between gap-3 flex-wrap">
            <span>
              <strong>Twilio not configured.</strong> WhatsApp and SMS reminders (manual buttons and the Sunday auto-scheduler) will fail until you add your Account SID, Auth Token and sender numbers.
            </span>
            <Button size="sm" variant="outline" className="border-amber-500 text-amber-800" onClick={() => { window.location.href = '/settings'; }} data-testid="renewals-twilio-fix">Set up Twilio →</Button>
          </AlertDescription>
        </Alert>
      )}

      {data[7].length > 0 && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            <strong>Urgent:</strong> {data[7].length} {data[7].length === 1 ? 'policy needs' : 'policies need'} renewal within 7 days!
          </AlertDescription>
        </Alert>
      )}

      {/* Policy Expiry Timeline — moved from Dashboard, lives here now */}
      <Card className="border-slate-200 shadow-sm" data-testid="policy-expiry-timeline">
        <CardHeader className="pb-2">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-blue-800 mr-2" />
            <CardTitle style={{ fontFamily: 'Manrope' }} className="text-base">Policy Expiry Timeline</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
            <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2.5" data-testid="expiry-7d">
              <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">7 Days</p>
              <p className="text-[22px] font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope' }}>{stats?.expiring_7_days || 0}</p>
            </div>
            <div className="rounded-lg border border-orange-100 bg-orange-50 px-3 py-2.5" data-testid="expiry-15d">
              <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">15 Days</p>
              <p className="text-[22px] font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope' }}>{stats?.expiring_15_days || 0}</p>
            </div>
            <div className="rounded-lg border border-yellow-100 bg-yellow-50 px-3 py-2.5" data-testid="expiry-30d">
              <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">30 Days</p>
              <p className="text-[22px] font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope' }}>{stats?.expiring_30_days || 0}</p>
            </div>
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5" data-testid="expiry-60d">
              <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">60 Days</p>
              <p className="text-[22px] font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope' }}>{stats?.expiring_60_days || 0}</p>
            </div>
            <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2.5" data-testid="expiry-90d">
              <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">90 Days</p>
              <p className="text-[22px] font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope' }}>{stats?.expiring_90_days || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center">
            <Calendar className="h-5 w-5 text-blue-800 mr-2" />
            <CardTitle style={{ fontFamily: 'Manrope' }}>Renewal Timeline</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelected({}); }} className="w-full">
            <TabsList className="mb-4 flex flex-wrap">
              {gracePolicies.length > 0 && (
                <TabsTrigger value="grace" className="text-red-700 data-[state=active]:bg-red-100" data-testid="tab-grace">
                  <Clock className="h-3.5 w-3.5 mr-1" />
                  Grace ({gracePolicies.length})
                </TabsTrigger>
              )}
              <TabsTrigger value="7days" data-testid="tab-7days">7 Days ({data[7].length})</TabsTrigger>
              <TabsTrigger value="15days" data-testid="tab-15days">15 Days ({data[15]?.length || 0})</TabsTrigger>
              <TabsTrigger value="30days" data-testid="tab-30days">30 Days ({data[30].length})</TabsTrigger>
              <TabsTrigger value="60days" data-testid="tab-60days">60 Days ({data[60].length})</TabsTrigger>
              <TabsTrigger value="90days" data-testid="tab-90days">90 Days ({data[90].length})</TabsTrigger>
              <TabsTrigger value="lost" className="text-red-700 data-[state=active]:bg-red-100" data-testid="tab-lost">
                <XCircle className="h-3.5 w-3.5 mr-1" />
                Lost ({lostRenewals.length})
              </TabsTrigger>
              <TabsTrigger value="custom" data-testid="tab-custom-range">Custom Range</TabsTrigger>
            </TabsList>
            {gracePolicies.length > 0 && (
              <TabsContent value="grace" data-testid="grace-tab-content">
                <Alert className="bg-red-50 border-red-300 mb-3">
                  <AlertCircle className="h-4 w-4 text-red-700" />
                  <AlertDescription className="text-red-900">
                    <strong>Grace Period — Action Needed.</strong> {gracePolicies.length} life policy {gracePolicies.length === 1 ? 'is' : 'are'} past due and still in the grace window. Renew before the countdown ends or it lapses.
                  </AlertDescription>
                </Alert>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Policy</TableHead><TableHead>Client</TableHead><TableHead>Product</TableHead>
                    <TableHead>Due</TableHead><TableHead>Overdue</TableHead><TableHead>Grace Left</TableHead><TableHead>Action</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {gracePolicies.map((g) => (
                      <TableRow key={g.policy_id} data-testid={`grace-row-${g.policy_id}`}>
                        <TableCell className="font-medium">{g.policy_number}</TableCell>
                        <TableCell><p className="font-medium">{g.client_name}</p><p className="text-xs text-slate-500">{g.client_phone}</p></TableCell>
                        <TableCell><p className="font-medium">{g.product_name}</p><p className="text-xs text-slate-500">{g.product_type}</p></TableCell>
                        <TableCell>{formatDate(g.next_due_date)}</TableCell>
                        <TableCell><Badge className="bg-red-100 text-red-800">{g.days_since_due}d</Badge></TableCell>
                        <TableCell>
                          <Badge className={g.days_left_in_grace <= 3 ? 'bg-red-600 text-white' : 'bg-orange-100 text-orange-800'} data-testid={`grace-countdown-${g.policy_id}`}>
                            {g.days_left_in_grace}d of {g.grace_period_days}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" className="border-green-600 text-green-700 hover:bg-green-50 h-7 px-2"
                            disabled={waSendingId === g.policy_id || !g.client_phone}
                            onClick={() => sendWhatsAppReminder({ ...g, days_remaining: -g.days_since_due })}
                            data-testid={`grace-wa-${g.policy_id}`}>
                            <MessageSquare className="h-3.5 w-3.5 mr-1" />
                            {waSendingId === g.policy_id ? 'Sending…' : 'WhatsApp'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>
            )}
            {BUCKETS.map(d => (
              <TabsContent key={d} value={`${d}days`} className="mt-4">
                <RenewalsTable bucket={d} />
              </TabsContent>
            ))}

            {/* Round 63 · Fix A — Lost tab. Read-only list (newest first). */}
            <TabsContent value="lost" className="mt-4" data-testid="lost-tab-content">
              {lostRenewals.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No renewals have been marked Lost yet.</p>
              ) : (
                <>
                  <Alert className="bg-red-50 border-red-200 mb-3">
                    <AlertCircle className="h-4 w-4 text-red-700" />
                    <AlertDescription className="text-red-900 flex items-center justify-between gap-2 flex-wrap">
                      <span>
                        <strong>{lostRenewals.length}</strong> renewal{lostRenewals.length === 1 ? ' is' : 's are'} marked Lost. These stay out of the active renewal buckets but keep flowing through <strong>Lost Leads</strong> for win-back.
                      </span>
                      <Button size="sm" variant="outline" className="border-red-500 text-red-800 h-7"
                        onClick={() => { window.location.href = '/lost-leads'; }}
                        data-testid="lost-tab-open-lost-leads">
                        Open Lost Leads →
                      </Button>
                    </AlertDescription>
                  </Alert>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Policy</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Insurer</TableHead>
                      <TableHead>Premium</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Lost On</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {lostRenewals.map((r) => (
                        <TableRow key={r.policy_id} data-testid={`lost-row-${r.policy_id}`}>
                          <TableCell className="font-medium">{r.policy_number}</TableCell>
                          <TableCell>
                            <p className="font-medium">{r.client_name}</p>
                            <p className="text-xs text-slate-500">{r.client_phone || '—'}</p>
                          </TableCell>
                          <TableCell>
                            <p className="font-medium">{r.product_name}</p>
                            <p className="text-xs text-slate-500">{r.product_type}</p>
                          </TableCell>
                          <TableCell>{r.insurer || '—'}</TableCell>
                          <TableCell>{r.premium_amount ? formatCurrency(r.premium_amount) : '—'}</TableCell>
                          <TableCell>
                            {r.loss_reason ? (
                              <span
                                className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-800 border border-red-200"
                                title={r.loss_notes || ''}
                                data-testid={`lost-reason-${r.policy_id}`}
                              >
                                {r.loss_reason}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-slate-600">{r.lost_at ? formatDate(r.lost_at) : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </TabsContent>
            <TabsContent value="custom" className="mt-4 space-y-3">
              <div className="flex flex-wrap items-end gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="renewals-custom-range-controls">
                <div className="space-y-1">
                  <Label className="text-xs">From</Label>
                  <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="renewals-custom-from" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To</Label>
                  <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="renewals-custom-to" />
                </div>
                <Button
                  onClick={async () => {
                    if (!customFrom || !customTo) { toast.error('Pick both From and To dates'); return; }
                    setCustomLoading(true);
                    try {
                      const { data: rows } = await axios.get(`${API}/renewals?date_from=${customFrom}&date_to=${customTo}`, { withCredentials: true });
                      setCustomRows(rows || []);
                      toast.success(`${rows.length} renewal${rows.length === 1 ? '' : 's'} in this range`);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to load range');
                    } finally { setCustomLoading(false); }
                  }}
                  className="bg-blue-800 hover:bg-blue-900"
                  disabled={customLoading}
                  data-testid="renewals-custom-apply"
                >
                  {customLoading ? 'Loading…' : 'Apply Range'}
                </Button>
                {customRows !== null && (
                  <span className="text-xs text-slate-500">Showing {customRows.length} renewal{customRows.length === 1 ? '' : 's'} between {customFrom} and {customTo}</span>
                )}
              </div>
              {customRows === null ? (
                <p className="text-center text-slate-500 py-8">Pick a From/To date range and click Apply to see renewals.</p>
              ) : customRows.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No renewals due in this window.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Policy Number</TableHead><TableHead>Client</TableHead><TableHead>Product</TableHead>
                      <TableHead>Premium</TableHead><TableHead>Due Date</TableHead><TableHead>Days Left</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customRows.map(r => (
                      <TableRow key={r.policy_id} data-testid={`renewal-custom-row-${r.policy_id}`}>
                        <TableCell className="font-medium">{r.policy_number}</TableCell>
                        <TableCell><p className="font-medium">{r.client_name}</p><p className="text-xs text-slate-500">{r.client_phone}</p></TableCell>
                        <TableCell><p className="font-medium">{r.product_name}</p><p className="text-xs text-slate-500">{r.product_type}</p></TableCell>
                        <TableCell>{formatCurrency(r.premium_amount)}</TableCell>
                        <TableCell>{formatDate(r.next_due_date)}</TableCell>
                        <TableCell><Badge className={getUrgencyColor(r.days_remaining)}>{r.days_remaining} days</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Bulk Send Dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Send · {selectedCount} client{selectedCount === 1 ? '' : 's'}</DialogTitle>
            <DialogDescription>Every client will get a copy of the chosen template with their own details substituted in.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Channel</Label>
                <Select value={bulkChannel} onValueChange={setBulkChannel}>
                  <SelectTrigger data-testid="bulk-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Template</Label>
                <Select value={bulkTemplateId} onValueChange={setBulkTemplateId}>
                  <SelectTrigger data-testid="bulk-template-select"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                  <SelectContent>
                    {bulkChannelTemplates.map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 max-h-40 overflow-y-auto">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Recipients</p>
              <div className="flex flex-wrap gap-2">
                {selectedRenewals().map(r => (
                  <Badge key={r.policy_id} variant="outline" className="text-xs">
                    {r.client_name} · {r.policy_number}
                  </Badge>
                ))}
              </div>
            </div>

            {bulkResult && (
              <div className="p-3 rounded-lg border border-slate-200 bg-white">
                <p className="text-sm mb-2">
                  <strong>Result:</strong>{' '}
                  <span className="text-green-700">{bulkResult.sent} sent</span> ·{' '}
                  <span className="text-red-700">{bulkResult.failed} failed</span> ·{' '}
                  <span className="text-slate-600">{bulkResult.skipped} skipped</span>
                </p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {bulkResult.details.map(d => (
                    <div key={d.client_id} className="flex items-center justify-between text-xs">
                      <span>{d.client_name || d.client_id}</span>
                      <Badge className={d.status === 'sent' ? 'bg-green-100 text-green-800' : d.status === 'skipped' ? 'bg-slate-100 text-slate-700' : 'bg-red-100 text-red-800'}>
                        {d.status}{d.error ? ` · ${d.error}` : ''}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setBulkOpen(false)}>Close</Button>
              <Button
                onClick={sendBulk}
                disabled={bulkSending || !bulkTemplateId}
                className="bg-blue-800 hover:bg-blue-900"
                data-testid="bulk-send-submit-button"
              >
                {bulkSending ? 'Sending...' : `Send to ${selectedCount} client${selectedCount === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Renewal Action Dialog: Mark Renewed / Lost */}
      <Dialog open={actionOpen} onOpenChange={setActionOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Update renewal · {actionPolicy?.policy_number}</DialogTitle>
            <DialogDescription>Mark this renewal as Renewed (capture the new term) or Lost.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" role="tablist">
              <button
                type="button"
                onClick={() => setActionMode('renewed')}
                className={`px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1 ${actionMode === 'renewed' ? 'bg-green-600 text-white shadow-sm' : 'text-slate-600'}`}
                data-testid="action-mode-renewed"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Renewed
              </button>
              <button
                type="button"
                onClick={() => setActionMode('lost')}
                className={`px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1 ${actionMode === 'lost' ? 'bg-red-600 text-white shadow-sm' : 'text-slate-600'}`}
                data-testid="action-mode-lost"
              >
                <XCircle className="h-3.5 w-3.5" /> Lost
              </button>
            </div>

            {actionMode === 'renewed' ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">New Start Date *</Label>
                  <input type="date" value={actionForm.new_start_date} onChange={(e) => onActionStartChange(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="action-start-input" />
                </div>
                <div className="space-y-1"><Label className="text-xs">New End Date</Label>
                  <input type="date" value={actionForm.new_end_date} onChange={(e) => setActionForm({ ...actionForm, new_end_date: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="action-end-input" />
                </div>
                <div className="space-y-1"><Label className="text-xs">Next Due Date</Label>
                  <input type="date" value={actionForm.new_next_due_date} onChange={(e) => setActionForm({ ...actionForm, new_next_due_date: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="action-due-input" />
                </div>
                <div className="space-y-1"><Label className="text-xs">Policy Number (if changed)</Label>
                  <input value={actionForm.new_policy_number} onChange={(e) => setActionForm({ ...actionForm, new_policy_number: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="action-policyno-input" />
                </div>
                <div className="space-y-1"><Label className="text-xs">Sum Assured (₹)</Label>
                  <input type="number" min="0" value={actionForm.new_sum_assured} onChange={(e) => setActionForm({ ...actionForm, new_sum_assured: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="action-sum-input" />
                </div>
                <div className="space-y-1"><Label className="text-xs">Net Premium (₹)</Label>
                  <input type="number" min="0" value={actionForm.new_premium_amount} onChange={(e) => setActionForm({ ...actionForm, new_premium_amount: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="action-premium-input" />
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-700 bg-red-50 border border-red-200 rounded p-3">
                This will mark the policy as <strong>Lapsed</strong> with a "lost" renewal outcome. You can still reopen it later.
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setActionOpen(false)}>Cancel</Button>
              <Button onClick={submitAction} disabled={actionSaving} className={actionMode === 'renewed' ? 'bg-green-700 hover:bg-green-800' : 'bg-red-700 hover:bg-red-800'} data-testid="action-submit-button">
                {actionSaving ? 'Saving…' : (actionMode === 'renewed' ? 'Mark Renewed' : 'Mark Lost')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Loss-reason picker for one-click "Mark Lost" on a renewal row */}
      <LossReasonDialog
        open={!!lostReasonTarget}
        title={lostReasonTarget ? `Mark policy ${lostReasonTarget.policy_number || ''} as Lost` : ''}
        description="Pick the closest reason so it clusters on the Lost Leads Analysis chart."
        confirming={lostReasonSaving}
        onCancel={() => setLostReasonTarget(null)}
        onConfirm={submitQuickMarkLost}
      />
    </div>
  );
};

export default Renewals;
